import { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router';

import { Source, useAppSelector } from '@store';

import { LOCAL_STORAGE_THEME } from './constants';
import { arrayWithUniques } from './tools';

type PathParams = {
	source: string;
	forwarder: string;
};

export const useDarkTheme = (watch: boolean = false): boolean => {
	const media = window.matchMedia('(prefers-color-scheme: dark)');
	const theme = useAppSelector(state => state.ui.theme);

	const getInitialTheme = () => {
		const savedTheme = localStorage.getItem(LOCAL_STORAGE_THEME);
		const reference = theme ?? savedTheme;
		return reference === 'auto' || !reference ? media.matches : reference === 'dark';
	};

	const condition = getInitialTheme();
	const [isDarkTheme, setIsDarkTheme] = useState(condition);

	useEffect(() => {
		if (!watch) {
			return;
		}

		const onChange = (e: MediaQueryListEvent) => {
			setIsDarkTheme(e.matches);
		};

		media.addEventListener('change', onChange);

		return () => {
			media.removeEventListener('change', onChange);
		};
	}, [media, watch]);

	useEffect(() => {
		if (theme) {
			localStorage.setItem(LOCAL_STORAGE_THEME, theme);
		}

		setIsDarkTheme(condition);
	}, [condition, theme]);

	useEffect(() => {
		if (watch) {
			document.documentElement.classList.toggle('dark-theme', isDarkTheme);
		}
	}, [isDarkTheme, watch]);

	return isDarkTheme;
};

export const useDataPage = (data: Source[], key: keyof PathParams) => {
	const params = useParams<PathParams>();
	const match = params[key];

	const [searchTerm, setSearchTerm] = useState('');
	const [selectedSymbol, setSelectedSymbol] = useState('');

	const staticData = useMemo(() => data.filter(item => item.static_source), [data]);
	const dynamicData = useMemo(() => data.filter(item => !item.static_source), [data]);

	const filteredData = useMemo(() => {
		if (searchTerm === '') {
			return dynamicData;
		}

		return dynamicData.filter(item => item.name_long.toLowerCase().includes(searchTerm.trim().toLowerCase()));
	}, [dynamicData, searchTerm]);

	const availableSymbols = useMemo(() => arrayWithUniques(filteredData, 'name_long'), [filteredData]);

	const initialItemDynamic = useMemo(() => {
		if (match) {
			return filteredData.findIndex(item => item.name_slug.toLowerCase() === match.toLowerCase());
		}

		return -1;
	}, [filteredData, key, params]);

	const initialItemStatic = useMemo(() => {
		if (match) {
			return staticData.findIndex(item => item.name_slug.toLowerCase() === match?.toLowerCase());
		}

		return -1;
	}, [key, params, staticData]);

	const selectedItem = useMemo(() => {
		if (!selectedSymbol) {
			return -1;
		}

		return filteredData.findIndex(item => item.name_long[0].toLowerCase() === selectedSymbol);
	}, [filteredData, selectedSymbol]);

	useEffect(() => {
		if (selectedSymbol) {
			return;
		}

		if (selectedItem > -1) {
			setSelectedSymbol(filteredData[selectedItem].name_long[0].toLowerCase());
		}

		if (!match) {
			setSelectedSymbol(availableSymbols[0]);
		}
	}, [availableSymbols, selectedSymbol, selectedItem, filteredData]);

	return {
		staticData,
		filteredData,
		availableSymbols,
		selectedItem,
		initialItemDynamic,
		initialItemStatic,
		selectedSymbol,
		setSearchTerm,
		setSelectedSymbol,
		searchTerm
	};
};
